import type { TranslationKey } from "@/lib/i18n/keys";
import type { DeedConflict, FactCheck } from "@/types";

/** A title deed the site ticks while the listing's own text says it is not
 *  there yet ("Sənəd: müqavilə", "kupça ilin sonuna veriləcək"; backend
 *  services/deed_claim, ticket 56). The listing then counts as having no
 *  deed: out of the deed filter, valued without one, and every surface
 *  that would say "deed" shows this warning instead (owner, 2026-10-28).
 *
 *  The seller's words are read, never shown (owner, 2026-11-01: "We should
 *  be able to parse if somebody writes this word, but not show it
 *  ourselves"): every surface says it in our own words. */

/** The flag. The API sends `deed_conflict`; `deed_conflict_phrase` is what
 *  it sent before 2026-11-01 (the seller's words), read as the flag only
 *  until every page renders from the new API. */
export function hasDeedConflict(x: {
  deed_conflict?: boolean | null;
  deed_conflict_phrase?: string | null;
}): boolean {
  return x.deed_conflict === true || !!x.deed_conflict_phrase;
}

/** The warning sentence, in our own words. */
export function deedClaimWarning(t: (key: TranslationKey) => string): string {
  return t("deed_claim.warning_plain");
}

/** The conflict an analysis result carries: its own field, else our
 *  finding in the findings (older results, the shared views). */
export function deedConflictOf(
  conflict: DeedConflict | null | undefined,
  checks: FactCheck[] | null | undefined,
): DeedConflict | null {
  if (conflict) return conflict;
  const own = (checks || []).find((c) => c.finding_type === "deed_conflict");
  return own ? { phrase: own.source_quote ?? null } : null;
}

/** Our deed-claim finding in our own words. Results stored before
 *  2026-11-01 quote the seller in its message («Kupça veriləcək»), and a
 *  shared or saved result shows the stored text; this one sentence replaces
 *  it, without the quote. */
export function inOwnWords<T extends FactCheck>(
  t: (key: TranslationKey) => string,
  checks: T[] | null | undefined,
): T[] {
  return (checks || []).map((c) => (c.finding_type === "deed_conflict"
    ? { ...c, message: t("deed_claim.finding"), source_quote: null }
    : c));
}
